import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import {
  CuttingClientOptions,
  CuttingOrderDetail,
  CuttingSettings,
  FilterParams,
  LegacyDialogsService,
  OrderListTableField,
  OrderStatusAction,
} from 'cloud-shared-lib';
import { Observable, Subject } from 'rxjs';
import { concatMap, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import { ClientOptionService } from '@cutting/main/shared/services/client-options.service';
import { OrderListService } from './order-list.service';
import { OrderTableFieldsDialogComponent } from './order-table-fields-dialog/order-table-fields-dialog.component';

@Component({
  selector: 'cutting-order-list',
  templateUrl: './order-list.component.html',
  styleUrls: ['./order-list.component.scss'],
  animations: fuseAnimations,
  encapsulation: ViewEncapsulation.None,
})
export class OrderListComponent extends FilterParams implements OnInit, OnDestroy {
  currentGroupId = 1;
  unsubscribe$: Subject<void> = new Subject();
  cuttingOptions: CuttingSettings;
  clientOptions: CuttingClientOptions;
  selectionMode = false;
  selection: SelectionModel<number> = new SelectionModel(true);
  agreementState: {
    actual: boolean;
    news: string;
    url: string;
  };
  isArchivePage = false;

  data$: Observable<CuttingOrderDetail[]>;

  originalFields = [
    'name',
    'factoryOrderId',
    'amount',
    'note',
    'clientNote',
    'statusName',
    'dateCreate',
    'dateEdit',
    'menu',
  ];

  displayedColumns = this.originalFields;

  mbExist$: Observable<boolean>;

  get totalCount(): number {
    return this.orderListService.totalCount;
  }

  constructor(
    public orderListService: OrderListService,
    public dialog: MatDialog,
    public dialogsService: LegacyDialogsService,
    public router: Router,
    public route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private clientSettingsService: ClientOptionService
  ) {
    super(route, router);
    this.filters = {
      searchText: '',
      pageIndex: 0,
      pageSize: 25,
      active: '',
      direction: '',
    };
    this.isArchivePage = route.snapshot.paramMap.get('filter') === 'archive';
    this.addListeners();
    this.data$ = this.orderListService.onOrdersChanged;
    this.mbExist$ = this.orderListService.hasMatBase();
    this.getClientOptions();
  }

  ngOnInit(): void {
    this.orderListService
      .checkAgreemenrAccept()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(a => (this.agreementState = a));
  }

  isEditableOrder(order: CuttingOrderDetail): boolean {
    return (
      order.status.action === OrderStatusAction.Create || order.status.action === OrderStatusAction.CuttingCompleted
    );
  }

  isConfirmedOrder(order: CuttingOrderDetail): boolean {
    return !this.isEditableOrder(order) && order.status.action !== OrderStatusAction.Ordering;
  }

  createOrderCopy(orderId: number): void {
    this.orderListService
      .createOrderCopy(orderId)
      .pipe(
        takeUntil(this.unsubscribe$),
        map(res => res.orderId)
      )
      .subscribe(
        id => {
          this.router.navigate(['/order/detail/' + id]);
        },
        () => {
          this.snackBar.open($localize`Не удалось создать копию заказа`, 'OK', { duration: 6000 });
        }
      );
  }

  async toggleArchived(order: CuttingOrderDetail): Promise<void> {
    const message = this.isArchivePage
      ? $localize`Вернуть заказ ${order.name} в основной список заказов`
      : $localize`Переместить заказ ${order.name} в архив?`;
    this.dialogsService
      .confirm(message)
      .pipe(
        filter(res => res),
        tap(() => {
          this.orderListService
            .toggleArchived(order.id, !this.isArchivePage)
            .then(() => {
              this.snackBar.open(
                this.isArchivePage ? $localize`Заказ восстановлен из архива` : $localize`Заказ перемещен в архив`
              );
              this.getOrders();
            })
            .catch((err: HttpErrorResponse) => {
              let message = '';

              if (this.isArchivePage) {
                message = $localize`Не удалось восстановить заказ из архива.`;

                if (err.status === 400) {
                  message += '<br>' + $localize`Файлы заказа удалены`;
                }
              } else {
                message = $localize`Не удалось переместить заказ в архив`;
              }

              this.snackBar.open(message);
            });
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }

  goToNewOrder(): void {
    if (this.agreementState.actual) {
      this.router.navigate(['/order/detail/new']);
    } else {
      const agreement =
        '<div>' +
        $localize`Соглашение изменено` +
        '</div>' +
        '<div class="mb-12">' +
        '<a href="' +
        this.agreementState.url +
        '" target="_blank" rel="noopener noreferrer">' +
        $localize`Полная версия соглашения` +
        '</a>' +
        '</div>' +
        '<div>' +
        $localize`Новое` +
        ':</div>' +
        '<pre class="pl-4">' +
        this.agreementState.news +
        '</pre>';

      this.dialogsService
        .confirm(agreement)
        .pipe(
          filter(res => res),
          concatMap(() => this.orderListService.acceptAgreement()),
          tap(() => this.router.navigate(['/order/detail/new'])),
          takeUntil(this.unsubscribe$)
        )
        .subscribe();
    }
  }

  uniteOrdersMode(): void {
    this.selectionMode = true;
    this.displayedColumns.unshift('selection');
  }

  uniteOrders(): void {
    this.orderListService.uniteOrders(this.selection.selected).subscribe(
      () => {
        this.snackBar.open($localize`Заказы объединены`);
        this.uniteCancel();

        this.getOrders();
      },
      () => {
        this.snackBar.open($localize`Не удалось объединить заказы`);
        this.uniteCancel();
      }
    );
  }

  uniteCancel(): void {
    this.selectionMode = false;
    this.selection.clear();

    this.displayedColumns.shift();
  }

  getOrders(): void {
    this.orderListService.getOrders(
      this.filters.pageIndex,
      this.filters.pageSize,
      this.filters.searchText,
      this.isArchivePage,
      this.filters.active,
      this.filters.direction
    );
  }

  openFieldSettingsDialog(): void {
    this.dialogsService
      .component(OrderTableFieldsDialogComponent, this.clientOptions.orderTableFields, 500)
      .pipe(
        filter(res => !!res),
        switchMap(res => this.clientSettingsService.saveOrderTableFieldSettings(res)),
        tap(() => this.getClientOptions()),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }

  updateDisplayedColumns(): void {
    const isVisible = (f: OrderListTableField): boolean => f.visible;

    this.displayedColumns =
      this.clientOptions.orderTableFields?.filter(isVisible).map(f => f.name) || this.originalFields;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }

  private addListeners(): void {
    this.urlChanged$
      .pipe(
        tap(() => this.getOrders()),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }

  private getClientOptions(): void {
    this.clientSettingsService
      .getClientOptions()
      .pipe(
        tap(res => {
          this.clientOptions = res;
          this.updateDisplayedColumns();
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }
}
